<template>
  <div class="influencer-list">
    <header class="page-header">
      <div class="header-main">
        <div class="header-icon">
          <i class="bi bi-people-fill" style="font-size:22px" aria-hidden="true"></i>
        </div>
        <div class="header-text">
          <div class="header-title-row">
            <h1 class="page-title">Influencers</h1>
            <span class="stat-chip" v-if="hasTotal">
              <span class="stat-dot"></span>
              <strong>{{ total.toLocaleString() }}</strong> total
            </span>
            <span class="stat-chip stat-chip-muted" v-if="hasActiveFilters">
              filtered
            </span>
          </div>
          <p class="page-subtitle" v-if="!loading && influencers.length">
            Showing <strong>{{ rangeStart }}–{{ rangeEnd }}</strong><template v-if="hasTotal"> of <strong>{{ total.toLocaleString() }}</strong></template> · sorted by <strong>{{ sortLabel }}</strong>
          </p>
          <p class="page-subtitle" v-else-if="loading">Loading influencers…</p>
          <p class="page-subtitle" v-else>No results match the current filters.</p>
        </div>
      </div>

      <div class="header-actions">
        <a href="#/profile/waiting/list" class="btn btn-ghost" title="Instagram profiles waiting for review">
          <i class="bi bi-hourglass-split" aria-hidden="true"></i>
          Pending profiles
        </a>
        <a href="#/influencer/new" class="btn btn-primary">
          <i class="bi bi-plus-lg" aria-hidden="true"></i>
          Add influencer
        </a>
      </div>
    </header>

    <div class="filters-card">
      <div class="filters-grid">
        <div class="filter-field filter-search">
          <label class="filter-label">Search</label>
          <div class="search-input">
            <i class="bi bi-search search-icon" aria-hidden="true"></i>
            <input
              type="text"
              class="form-control"
              v-model="filter.name"
              placeholder="Name, nickname…"
              @keyup.enter="resetAndFetch"
            >
            <button class="clear-btn" v-if="filter.name" @click="clearSearch" title="Clear">
              <i class="bi bi-x" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div class="filter-field">
          <label class="filter-label">Category</label>
          <select class="form-control" v-model="filter.category_id">
            <option :value="0">All categories</option>
            <option v-for="(c, id) in category" :key="id" :value="id">{{ c }}</option>
          </select>
        </div>

        <div class="filter-field">
          <label class="filter-label">Sort by</label>
          <select class="form-control" v-model="sort">
            <option value="id,desc">Newest first</option>
            <option value="id,asc">Oldest first</option>
            <option value="name,asc">Name A→Z</option>
            <option value="name,desc">Name Z→A</option>
          </select>
        </div>

        <div class="filter-field filter-actions">
          <button class="btn btn-outline-secondary btn-reset" @click="resetFilters" :disabled="!hasActiveFilters">
            Reset
          </button>
        </div>
      </div>

      <div class="filter-chips" v-if="hasActiveFilters">
        <span class="chip" v-if="filter.name">
          Search: <strong>{{ filter.name }}</strong>
          <button @click="clearSearch">×</button>
        </span>
        <span class="chip" v-if="filter.category_id && category[filter.category_id]">
          Category: <strong>{{ category[filter.category_id] }}</strong>
          <button @click="filter.category_id = 0">×</button>
        </span>
      </div>
    </div>

    <div class="table-card" :class="{ 'is-loading': loading }">
      <div class="table-wrap" v-if="!loading && influencers.length">
        <table class="data-table">
          <thead>
            <tr>
              <th class="col-id">#</th>
              <th class="col-name">Name</th>
              <th class="col-person">Person</th>
              <th class="col-audience">Audience</th>
              <th class="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="influencer in influencers" :key="influencer.id">
              <td class="col-id">
                <span class="id-badge">#{{ influencer.id }}</span>
              </td>
              <td class="col-name">
                <a class="name-link" :href="getEditLink(influencer.id)">{{ influencer.name }}</a>
                <span class="nickname" v-if="influencer.nickname">{{ influencer.nickname }}</span>
              </td>
              <td class="col-person">
                <span v-if="influencer.firstname || influencer.lastname">
                  {{ [influencer.firstname, influencer.lastname].filter(Boolean).join(' ') }}
                </span>
                <span v-else class="muted">—</span>
              </td>
              <td class="col-audience">
                <div class="audience-links">
                  <a
                    v-if="influencer.instagram_user !== undefined"
                    class="audience-pill audience-instagram"
                    target="_blank"
                    :href="'/#/audience/create/' + influencer.instagram_user.instagram_id + '/' + influencer.instagram_user.username"
                    :title="'Instagram: @' + influencer.instagram_user.username"
                  >
                    <img class="audience-icon" src="./../../assets/platforms/instagram.png"/>
                    <span>{{ influencer.instagram_user.username }}</span>
                  </a>
                  <a
                    v-if="influencer.tiktok_user !== undefined"
                    class="audience-pill audience-tiktok"
                    target="_blank"
                    :href="'/#/audience/tiktok/create/' + influencer.tiktok_user.tiktok_id_string + '/' + influencer.tiktok_user.username"
                    :title="'TikTok: @' + influencer.tiktok_user.username"
                  >
                    <img class="audience-icon" src="./../../assets/platforms/tiktok.png"/>
                    <span>{{ influencer.tiktok_user.username }}</span>
                  </a>
                  <span class="muted" v-if="influencer.instagram_user === undefined && influencer.tiktok_user === undefined">—</span>
                </div>
              </td>
              <td class="col-actions">
                <div class="row-actions">
                  <a class="btn-row-ask" :href="getAskLink(influencer.id)" title="Ask AI about this influencer">
                    <i class="bi bi-stars" aria-hidden="true"></i>
                    Ask AI
                  </a>
                  <a class="btn btn-outline-primary btn-sm btn-edit" :href="getEditLink(influencer.id)">
                    <i class="bi bi-pencil" aria-hidden="true"></i>
                    Edit
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="skeleton-list" v-if="loading">
        <div class="skeleton-row" v-for="n in 6" :key="'sk' + n">
          <div class="skeleton skeleton-id"></div>
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line skeleton-line-lg"></div>
            <div class="skeleton skeleton-line skeleton-line-sm"></div>
          </div>
          <div class="skeleton skeleton-line"></div>
          <div class="skeleton skeleton-pill"></div>
          <div class="skeleton skeleton-btn"></div>
        </div>
      </div>

      <div class="empty-state" v-if="!loading && !influencers.length">
        <i class="bi bi-search" style="font-size:48px" aria-hidden="true"></i>
        <h3>No influencers found</h3>
        <p v-if="hasActiveFilters">Try changing or resetting your filters.</p>
        <p v-else>Start by adding the first influencer.</p>
        <button v-if="hasActiveFilters" class="btn btn-outline-secondary" @click="resetFilters">Reset filters</button>
        <a v-else href="#/influencer/new" class="btn btn-primary">Add influencer</a>
      </div>
    </div>

    <div class="pagination-bar" v-if="influencers.length || page > 1">
      <div class="pagination-left">
        <label class="per-page-label">Per page</label>
        <select class="form-control per-page" v-model.number="limit">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>

      <div class="pagination-center">
        <button class="page-btn" @click="goToPage(page - 1)" :disabled="page <= 1">
          <i class="bi bi-chevron-left" aria-hidden="true"></i>
          Previous
        </button>

        <template v-if="hasTotal">
          <button
            v-for="p in visiblePages"
            :key="p.key"
            class="page-btn page-num"
            :class="{ 'is-active': p.value === page, 'is-ellipsis': p.ellipsis }"
            :disabled="p.ellipsis"
            @click="!p.ellipsis && goToPage(p.value)"
          >{{ p.label }}</button>
        </template>
        <span class="page-indicator" v-else>Page <strong>{{ page }}</strong></span>

        <button class="page-btn" @click="goToPage(page + 1)" :disabled="!hasNextPage">
          Next
          <i class="bi bi-chevron-right" aria-hidden="true"></i>
        </button>
      </div>

      <div class="pagination-right">
        <span v-if="hasTotal">Page {{ page }} / {{ totalPages }}</span>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: "influencers",
    data() {
      return {
        influencers: [],
        category: {},
        limit: 20,
        page: 1,
        total: 0,
        sort: 'id,desc',
        loading: false,
        searchTimer: null,
        filter: {
          category_id: 0,
          name: ''
        },
        apiHost: this.$appSettings.getSetting('host')
      }
    },
    computed: {
      hasActiveFilters() {
        return this.filter.name.trim() !== '' || this.filter.category_id !== 0;
      },
      hasTotal() {
        return this.total > 0;
      },
      totalPages() {
        return this.hasTotal ? Math.max(1, Math.ceil(this.total / this.limit)) : 1;
      },
      rangeStart() {
        if (!this.influencers.length) return 0;
        return (this.page - 1) * this.limit + 1;
      },
      rangeEnd() {
        return (this.page - 1) * this.limit + this.influencers.length;
      },
      hasNextPage() {
        if (this.hasTotal) return this.page < this.totalPages;
        return this.influencers.length === this.limit;
      },
      sortLabel() {
        const labels = {
          'id,desc': 'newest first',
          'id,asc': 'oldest first',
          'name,asc': 'name A→Z',
          'name,desc': 'name Z→A'
        };
        return labels[this.sort] || this.sort;
      },
      visiblePages() {
        const pages = [];
        const total = this.totalPages;
        const current = this.page;
        const push = (value, opts = {}) => pages.push({
          key: opts.ellipsis ? 'e' + value : 'p' + value,
          value,
          label: opts.ellipsis ? '…' : String(value),
          ellipsis: !!opts.ellipsis
        });

        if (total <= 7) {
          for (let i = 1; i <= total; i++) push(i);
          return pages;
        }

        push(1);
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        if (start > 2) push(start, { ellipsis: true });
        for (let i = start; i <= end; i++) push(i);
        if (end < total - 1) push(end, { ellipsis: true });
        push(total);
        return pages;
      }
    },
    watch: {
      'filter.name'() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.resetAndFetch(), 350);
      },
      'filter.category_id'() {
        this.resetAndFetch();
      },
      sort() {
        this.resetAndFetch();
      },
      limit() {
        this.resetAndFetch();
      }
    },
    methods: {
      fetchInfluencers() {
        this.loading = true;
        const [sortField, sortType] = this.sort.split(',');
        const offset = (this.page - 1) * this.limit;
        this.$http.get(this.apiHost + 'influencers/', {
          params: {
            limit: this.limit,
            offset: offset,
            sort_field: sortField,
            sort_type: sortType,
            filter: this.filter,
            token: this.$auth.getToken()
          }
        }).then(response => {
          this.loading = false;
          const data = response.body.data;
          this.influencers = data.influencers || [];
          this.category = data.category || this.category;
          if (typeof data.total === 'number') {
            this.total = data.total;
          } else if (typeof data.count === 'number') {
            this.total = data.count;
          } else {
            this.total = 0;
          }
        }, () => {
          this.loading = false;
        });
      },
      resetAndFetch() {
        this.page = 1;
        this.fetchInfluencers();
      },
      goToPage(p) {
        if (p < 1) return;
        if (this.hasTotal && p > this.totalPages) return;
        if (!this.hasTotal && p > this.page && !this.hasNextPage) return;
        this.page = p;
        this.fetchInfluencers();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      clearSearch() {
        this.filter.name = '';
      },
      resetFilters() {
        this.filter.name = '';
        this.filter.category_id = 0;
      },
      getEditLink(influencerId) {
        return '#/influencer/edit/' + influencerId;
      },
      getAskLink(influencerId) {
        return '#/influencer/ask/' + influencerId;
      }
    },
    created() {
      this.fetchInfluencers();
    }
  }
</script>

<style scoped>
  .influencer-list {
    max-width: 1180px;
    margin: 0 auto;
    color: #1f2937;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 24px;
    padding: 22px 24px;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 60%);
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    position: relative;
    overflow: hidden;
  }
  .header-main {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex: 1;
    min-width: 0;
  }
  .header-icon {
    flex: 0 0 48px;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #eaf1f9;
    color: #265a88;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .header-text {
    min-width: 0;
    flex: 1;
  }
  .header-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }
  .page-title {
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0;
    color: #0f172a;
    line-height: 1.1;
  }
  .stat-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #475569;
    border-radius: 999px;
    padding: 3px 10px 3px 8px;
    font-size: 12px;
    font-weight: 500;
  }
  .stat-chip strong {
    color: #0f172a;
    font-weight: 700;
  }
  .stat-chip-muted {
    background: #fef3c7;
    border-color: #fde68a;
    color: #92400e;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 10.5px;
    font-weight: 700;
    padding: 3px 10px;
  }
  .stat-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
  }
  .page-subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
  }
  .page-subtitle strong {
    color: #0f172a;
    font-weight: 600;
  }
  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .btn-ghost {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 1px solid #e5e7eb;
    color: #475569;
    font-weight: 500;
    padding: 8px 14px;
    border-radius: 8px;
    text-decoration: none;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .btn-ghost:hover {
    border-color: #265a88;
    color: #265a88;
    background: #fff;
  }
  .filters-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 18px 20px;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .filters-grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr auto;
    gap: 14px;
    align-items: end;
  }
  .filter-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 6px;
  }
  .form-control {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 9px 12px;
    font-size: 14px;
    background: #fff;
    color: #0f172a;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .form-control:focus {
    border-color: #265a88;
    box-shadow: 0 0 0 3px rgba(38, 90, 136, 0.12);
    outline: none;
  }
  .search-input {
    position: relative;
  }
  .search-icon {
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
    color: #6b7280;
    pointer-events: none;
  }
  .search-input .form-control {
    padding-left: 36px;
    padding-right: 32px;
  }
  .clear-btn {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    background: transparent;
    border: 0;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .clear-btn:hover {
    background: #f3f4f6;
    color: #0f172a;
  }
  .filter-actions {
    display: flex;
    align-items: flex-end;
  }
  .btn-reset {
    width: 100%;
    padding: 9px 14px;
    font-weight: 500;
  }
  .btn-reset:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .filter-chips {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px dashed #e5e7eb;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #eaf1f9;
    color: #265a88;
    border-radius: 999px;
    padding: 4px 4px 4px 12px;
    font-size: 12.5px;
  }
  .chip strong {
    font-weight: 600;
  }
  .chip button {
    background: rgba(38, 90, 136, 0.12);
    border: 0;
    color: #265a88;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    transition: background 0.15s;
  }
  .chip button:hover {
    background: rgba(38, 90, 136, 0.22);
  }

  .table-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    transition: opacity 0.2s;
  }
  .table-wrap {
    overflow-x: auto;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
  }
  .data-table thead th {
    background: #fafbfc;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    padding: 12px 16px;
    font-size: 11.5px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .data-table tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 14px;
    vertical-align: middle;
  }
  .data-table tbody tr:last-child td {
    border-bottom: 0;
  }
  .data-table tbody tr {
    transition: background 0.1s;
  }
  .data-table tbody tr:hover {
    background: #fafbfc;
  }

  .col-id { width: 80px; }
  .col-actions { width: 200px; text-align: right; }
  .col-audience { width: 280px; }
  .row-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    justify-content: flex-end;
  }
  .btn-row-ask {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #265a88 0%, #833ab4 100%);
    color: #fff;
    border-radius: 6px;
    padding: 5px 11px;
    font-size: 12.5px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    transition: filter 0.15s;
  }
  .btn-row-ask:hover {
    color: #fff;
    filter: brightness(1.08);
    color: #fff;
  }

  .id-badge {
    display: inline-block;
    background: #f1f5f9;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 6px;
  }
  .name-link {
    display: block;
    color: #0f172a;
    font-weight: 600;
    text-decoration: none;
    font-size: 14.5px;
  }
  .name-link:hover {
    color: #265a88;
    text-decoration: underline;
  }
  .nickname {
    display: block;
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }
  .muted {
    color: #9ca3af;
  }
  .audience-links {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .audience-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 4px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    font-size: 12.5px;
    color: #0f172a;
    text-decoration: none;
    max-width: 180px;
    transition: border-color 0.15s, background 0.15s;
  }
  .audience-pill span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .audience-pill:hover {
    border-color: #265a88;
    background: #fff;
  }
  .audience-icon {
    width: 20px;
    height: 20px;
    border-radius: 4px;
  }
  .btn-edit {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    padding: 5px 12px;
  }

  .skeleton-list {
    padding: 8px 0;
  }
  .skeleton-row {
    display: grid;
    grid-template-columns: 80px 1fr 180px 280px 180px;
    gap: 16px;
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    align-items: center;
  }
  .skeleton {
    background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 6px;
  }
  .skeleton-id { width: 50px; height: 22px; }
  .skeleton-line { height: 12px; width: 70%; }
  .skeleton-line-lg { width: 60%; height: 14px; }
  .skeleton-line-sm { width: 35%; margin-top: 6px; }
  .skeleton-stack > * + * { margin-top: 4px; }
  .skeleton-pill { width: 120px; height: 26px; border-radius: 999px; }
  .skeleton-btn { width: 70px; height: 28px; justify-self: end; }
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .empty-state {
    padding: 60px 20px;
    text-align: center;
    color: #6b7280;
  }
  .empty-state .bi {
    margin-bottom: 14px;
  }
  .empty-state h3 {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 6px;
  }
  .empty-state p {
    margin: 0 0 16px;
    font-size: 13.5px;
  }

  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 4px;
    margin-top: 4px;
  }
  .pagination-left, .pagination-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    font-size: 13px;
    color: #6b7280;
  }
  .pagination-right {
    justify-content: flex-end;
  }
  .per-page-label {
    margin: 0;
  }
  .per-page {
    width: auto;
    min-width: 70px;
    padding: 6px 28px 6px 10px;
  }
  .pagination-center {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .page-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #374151;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
    min-width: 36px;
    justify-content: center;
  }
  .page-btn:hover:not(:disabled):not(.is-active) {
    border-color: #265a88;
    color: #265a88;
  }
  .page-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .page-btn.is-active {
    background: #265a88;
    color: #fff;
    border-color: #265a88;
  }
  .page-btn.is-ellipsis {
    border-color: transparent;
    background: transparent;
    opacity: 1;
    cursor: default;
  }
  .page-indicator {
    font-size: 13px;
    color: #6b7280;
    padding: 0 12px;
  }
  .page-indicator strong {
    color: #0f172a;
    font-weight: 600;
  }

  .table-card.is-loading .table-wrap {
    display: none;
  }

  @media (max-width: 900px) {
    .filters-grid {
      grid-template-columns: 1fr 1fr;
    }
    .filter-search {
      grid-column: 1 / -1;
    }
    .col-audience { width: auto; }
    .skeleton-row {
      grid-template-columns: 60px 1fr 1fr 80px;
    }
    .skeleton-row > :nth-child(4) { display: none; }
  }
  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
      padding: 18px 18px;
    }
    .header-actions {
      width: 100%;
    }
    .header-actions .btn,
    .header-actions a {
      flex: 1;
      justify-content: center;
    }
  }
  @media (max-width: 640px) {
    .header-icon {
      flex: 0 0 40px;
      width: 40px;
      height: 40px;
    }
    .page-title {
      font-size: 22px;
    }
    .filters-grid {
      grid-template-columns: 1fr;
    }
    .pagination-bar {
      flex-direction: column;
      align-items: stretch;
    }
    .pagination-left, .pagination-right {
      justify-content: center;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/influencer/Influencers.vue